// Disposition: ein Einsatz gegen Fahrzeuge, Anhänger, Führerscheine und die
// Regeln des Betriebs prüfen.
//
// Die Regeln kommen als feste Form aus dem Gedächtnis (die KI übersetzt jeden
// Satz einmal, Function `ai` → einsatz-regeln). Geprüft wird hier, ohne KI:
//   verbot          — diese Personen dürfen diese Fahrzeuge nicht fahren
//   nur             — nur diese Personen dürfen diese Fahrzeuge fahren
//   allein_verboten — diese Personen nie ohne jemand anderen einplanen
//   hinweis         — betrifft die Planung, lässt sich aber nicht prüfen
//
// Ergebnis: Liste von { schwere, text, details?, regel? }
//   sperre  — eure Regel oder Gesetz verletzt; speichern nur bewusst
//   warnung — sollte man wissen
//   hinweis — eure Regel, die hier betroffen ist

import { istMinderjaehrig } from './zeit.js';

const SCHWERE = { sperre: 0, warnung: 1, hinweis: 2 };

const liste = (x) => (Array.isArray(x) ? x : []).map(String);
const gleich = (a, b) => String(a) === String(b);
export const istAnhaenger = (f) => f?.art === 'anhaenger';
export const fahrzeugName = (f) => String(f?.name || 'Fahrzeug').trim().replace(/\s+/g, ' ');
const vorname = (m) => String(m?.vorname || m?.name || '').trim().split(/\s+/)[0];
const namen = (ms) => ms.map(vorname).join(', ');
const kg = (n) => (n >= 1000 ? `${(n / 1000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} t` : `${n} kg`);
const datum = (iso) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

export function einsatzFahrzeugIds(e) {
    const ids = liste(e?.fahrzeug_ids);
    return ids.length ? ids : (e?.fahrzeug_id ? [String(e.fahrzeug_id)] : []);
}

export function einsatzMitarbeiterIds(e) {
    const ids = liste(e?.mitarbeiter_ids);
    return ids.length ? ids : (e?.mitarbeiter_id ? [String(e.mitarbeiter_id)] : []);
}

// ─── Führerschein ───────────────────────────────────────────────────────────

// Wer die linke Klasse hat, darf auch alles rechts davon fahren.
const SCHLIESST_EIN = {
    CE: ['C', 'C1E', 'C1', 'BE', 'B96', 'B'],
    C: ['C1', 'B'],
    C1E: ['C1', 'BE', 'B96', 'B'],
    C1: ['B'],
    BE: ['B96', 'B'],
    B96: ['B'],
};

/** Klassen aus den Fähigkeiten („Führerschein BE“, „Führerschein C1E“ …). */
export function fuehrerscheine(m) {
    const klassen = new Set();
    const dazu = (k) => {
        if (klassen.has(k)) return;
        klassen.add(k);
        (SCHLIESST_EIN[k] || []).forEach(dazu);
    };
    for (const q of m?.qualifikationen || []) {
        const k = /f(?:ü|ue|u)hrerschein\s*(?:klasse\s*)?([a-z0-9]+)/i.exec(String(q))?.[1]?.toUpperCase();
        if (k) dazu(k);
    }
    return klassen;
}

/**
 * Nötige Klasse für Fahrzeug + Anhänger. B bis 3,5 t, C1 bis 7,5 t, darüber C.
 * Anhänger über 750 kg: bei B bis 3,5 t Zug gesamt noch B, bis 4,25 t B96,
 * sonst BE; bei C1/C entsprechend C1E/CE.
 */
export function noetigeKlasse(fahrzeug, anhaenger) {
    const g = Number(fahrzeug?.zul_gesamtgewicht_kg) || 0;
    const basis = g > 7500 ? 'C' : g > 3500 ? 'C1' : 'B';
    const a = Number(anhaenger?.zul_gesamtgewicht_kg) || 0;
    if (!anhaenger || a <= 750) return basis;
    if (basis !== 'B') return `${basis}E`;
    // Gewicht des Zugfahrzeugs unbekannt → wie ein voller 3,5-Tonner
    const zug = (g || 3500) + a;
    return zug <= 3500 ? 'B' : zug <= 4250 ? 'B96' : 'BE';
}

// Klasse B setzen wir voraus, solange beim Mitarbeiter kein Führerschein
// eingetragen ist. Alles darüber muss eingetragen sein.
export function hatKlasse(m, klasse) {
    const k = fuehrerscheine(m);
    if (klasse === 'B') return k.size === 0 || k.has('B');
    return k.has(klasse);
}

// ─── Regeln ─────────────────────────────────────────────────────────────────

function betrifftFahrzeug(r, f) {
    const ausser = liste(r.alle_fahrzeuge_ausser);
    // „Azubis dürfen nur den Crafter fahren“ meint Fahrzeuge, keine Anhänger
    if (ausser.length) return !istAnhaenger(f) && !ausser.includes(String(f.id));
    return liste(r.fahrzeug_ids).includes(String(f.id));
}

function betrifftPerson(r, m) {
    return liste(r.mitarbeiter_ids).includes(String(m.id))
        || (r.beschaeftigungsarten || []).includes(m.beschaeftigungsart);
}

/** Regeln, die m verbieten, f zu fahren. Leer = darf. */
export function regelnGegenFahrer(m, f, regeln = []) {
    return regeln.filter((r) => betrifftFahrzeug(r, f) && (
        (r.art === 'verbot' && betrifftPerson(r, m))
        || (r.art === 'nur' && !betrifftPerson(r, m))
    ));
}

// Jedes Fahrzeug braucht einen eigenen Fahrer (Zuordnung per Augmentierung).
function fahrerZuordnen(eignung) {
    const faehrt = new Map();       // mitarbeiter.id → Index des Fahrzeugs
    const fahrer = new Map();       // fahrzeug.id → mitarbeiter
    const versuche = (i, besucht) => {
        for (const p of eignung[i].personen) {
            if (!p.darf || besucht.has(p.m.id)) continue;
            besucht.add(p.m.id);
            const j = faehrt.get(p.m.id);
            if (j === undefined || versuche(j, besucht)) {
                faehrt.set(p.m.id, i);
                fahrer.set(eignung[i].f.id, p.m);
                return true;
            }
        }
        return false;
    };
    eignung.forEach((_, i) => versuche(i, new Set()));
    return fahrer;
}

function ueberschneiden(a, b) {
    const aBis = a.end_date || a.start_date;
    const bBis = b.end_date || b.start_date;
    if (!a.start_date || !b.start_date || a.start_date > bBis || b.start_date > aBis) return false;
    // Am selben Tag nacheinander (vormittags / nachmittags) ist kein Konflikt
    if (a.start_time && a.end_time && b.start_time && b.end_time && a.start_date === aBis && b.start_date === bBis) {
        return a.start_time < b.end_time && b.start_time < a.end_time;
    }
    return true;
}

// ─── Prüfung ────────────────────────────────────────────────────────────────

/**
 * @param einsatz   { id?, mitarbeiter_ids, fahrzeug_ids, start_date, end_date, start_time?, end_time? }
 * @param kontext   { mitarbeiter, fahrzeuge, einsaetze?, regeln?, projekte? }
 */
export function pruefeEinsatz(einsatz, { mitarbeiter = [], fahrzeuge = [], einsaetze = [], regeln = [], projekte = [] } = {}) {
    const team = einsatzMitarbeiterIds(einsatz).map((id) => mitarbeiter.find((m) => gleich(m.id, id))).filter(Boolean);
    const gewaehlt = einsatzFahrzeugIds(einsatz).map((id) => fahrzeuge.find((f) => gleich(f.id, id))).filter(Boolean);
    const autos = gewaehlt.filter((f) => !istAnhaenger(f));
    const haenger = gewaehlt.filter(istAnhaenger);
    const von = einsatz.start_date;
    const bis = einsatz.end_date || von;
    const out = [];
    const regelHinweise = new Set();

    // 1. Anhänger an ein Zugfahrzeug mit Kupplung und genug Anhängelast
    const zug = new Map();
    for (const a of haenger) {
        const last = Number(a.zul_gesamtgewicht_kg) || 0;
        const kann = autos.filter((f) => !zug.has(f.id) && f.anhaengerkupplung
            && (!f.anhaengelast_kg || !last || Number(f.anhaengelast_kg) >= last));
        if (kann.length) {
            zug.set(kann[0].id, a);
            continue;
        }
        const mitKupplung = autos.filter((f) => f.anhaengerkupplung);
        out.push({
            schwere: 'sperre',
            text: !autos.length
                ? `${fahrzeugName(a)}: kein Zugfahrzeug eingeplant.`
                : !mitKupplung.length
                    ? `${fahrzeugName(a)}: kein eingeplantes Fahrzeug hat eine Anhängerkupplung.`
                    : `${fahrzeugName(a)} wiegt bis ${kg(last)} — mehr als die Anhängelast von ${mitKupplung.map((f) => `${fahrzeugName(f)} (${kg(Number(f.anhaengelast_kg))})`).join(', ')}.`,
        });
    }
    for (const a of haenger) {
        if (!a.zul_gesamtgewicht_kg) out.push({ schwere: 'warnung', text: `${fahrzeugName(a)}: Gesamtgewicht fehlt — Führerschein und Anhängelast nicht prüfbar.` });
    }

    // 2. Wer darf fahren?
    if (team.length && autos.length) {
        const eignung = autos.map((f) => {
            const a = zug.get(f.id);
            const klasse = noetigeKlasse(f, a);
            const personen = team.map((m) => {
                const gruende = [];
                if (m.geburtsdatum && istMinderjaehrig(m.geburtsdatum, von)) gruende.push('minderjährig');
                else if (!hatKlasse(m, klasse)) gruende.push(`kein Führerschein ${klasse === 'B96' ? 'B96/BE' : klasse}`);
                const verletzt = [...regelnGegenFahrer(m, f, regeln), ...(a ? regelnGegenFahrer(m, a, regeln) : [])];
                for (const r of verletzt) gruende.push(`Eure Regel: „${r.satz}“`);
                return { m, darf: gruende.length === 0, gruende };
            });
            return { f, a, personen };
        });

        const fahrer = fahrerZuordnen(eignung);
        for (const e of eignung) {
            if (fahrer.has(e.f.id)) continue;
            const titel = e.a ? `${fahrzeugName(e.f)} mit ${fahrzeugName(e.a)}` : fahrzeugName(e.f);
            const duerfen = e.personen.filter((p) => p.darf);
            if (duerfen.length) {
                out.push({ schwere: 'sperre', text: `${titel}: kein eigener Fahrer — ${namen(duerfen.map((p) => p.m))} fährt schon ein anderes Fahrzeug.` });
                continue;
            }
            // Gleiche Gründe zusammenfassen: „Kevin, Paula — Eure Regel: …“
            const nachGrund = new Map();
            for (const p of e.personen) {
                for (const g of p.gruende) nachGrund.set(g, [...(nachGrund.get(g) || []), p.m]);
            }
            out.push({
                schwere: 'sperre',
                text: `${titel}: niemand im Team darf fahren.`,
                details: [...nachGrund].map(([g, ms]) => `${namen(ms)} — ${g}`),
            });
        }
    }

    // 3. Personen, die nicht allein raus dürfen
    for (const r of regeln.filter((x) => x.art === 'allein_verboten')) {
        const betroffen = team.filter((m) => betrifftPerson(r, m));
        if (betroffen.length && betroffen.length === team.length) {
            out.push({ schwere: 'sperre', text: `${namen(betroffen)} ohne Begleitung eingeplant.`, regel: r.satz });
        }
    }

    // 4. Fahrzeug schon woanders verplant
    if (von) {
        for (const f of gewaehlt) {
            const andere = einsaetze.filter((x) => !x.is_deleted && !gleich(x.id, einsatz.id)
                && einsatzFahrzeugIds(x).includes(String(f.id)) && ueberschneiden({ ...einsatz, end_date: bis }, x));
            if (!andere.length) continue;
            const wo = andere.map((x) => {
                const p = projekte.find((pp) => gleich(pp.id, x.project_id));
                const tag = x.start_date === (x.end_date || x.start_date) ? datum(x.start_date) : `${datum(x.start_date)} – ${datum(x.end_date)}`;
                return p?.project_name ? `${p.project_name} (${tag})` : tag;
            });
            out.push({ schwere: 'sperre', text: `${fahrzeugName(f)} ist schon verplant: ${wo.join(', ')}.` });
        }
    }

    // 5. Sitzplätze — nur, wenn bei allen Fahrzeugen eingetragen
    if (team.length && autos.length && autos.every((f) => f.sitzplaetze)) {
        const sitze = autos.reduce((s, f) => s + Number(f.sitzplaetze), 0);
        if (sitze < team.length) out.push({ schwere: 'warnung', text: `${team.length} Leute, aber nur ${sitze} Sitzplätze.` });
    }

    // 6. HU
    if (von) {
        for (const f of gewaehlt.filter((x) => x.hu_bis)) {
            const hu = String(f.hu_bis).slice(0, 10);
            if (hu < von) out.push({ schwere: 'sperre', text: `${fahrzeugName(f)}: HU abgelaufen seit ${datum(hu)}.` });
            else if (hu < bis) out.push({ schwere: 'warnung', text: `${fahrzeugName(f)}: HU läuft am ${datum(hu)} ab.` });
        }
    }

    // 7. Regeln, die sich nicht prüfen lassen, aber hier betroffen sind — und
    //    Regeln, bei denen unklar ist, wer gemeint ist
    // Regeln zu Fahrzeugen erst, wenn eins davon gewählt ist; sonst über die Personen
    const betroffen = (r) => (liste(r.fahrzeug_ids).length || liste(r.alle_fahrzeuge_ausser).length
        ? gewaehlt.some((f) => betrifftFahrzeug(r, f))
        : team.some((m) => betrifftPerson(r, m)));
    for (const r of regeln) {
        if (!betroffen(r)) continue;
        if (r.art === 'hinweis' && !regelHinweise.has(r.satz)) {
            regelHinweise.add(r.satz);
            out.push({ schwere: 'hinweis', text: r.satz, regel: r.satz });
        }
        if (r.unklar) out.push({ schwere: 'hinweis', text: `Regel unklar: ${r.unklar}`, regel: r.satz });
    }

    return out.sort((a, b) => SCHWERE[a.schwere] - SCHWERE[b.schwere]);
}

export const hatSperre = (pruefung) => pruefung.some((p) => p.schwere === 'sperre');

/**
 * Fahrzeuge für einen Planungsvorschlag: die Wahl der KI, wenn sie besteht —
 * sonst das erste freie Fahrzeug, das jemand im Team fahren darf und in das
 * alle passen. Bewertet wird nur, was das Fahrzeug an Sperren dazubringt.
 */
export function fahrzeugeFuerVorschlag(vorschlag, kontext) {
    const basis = { ...vorschlag, end_date: vorschlag.end_date || vorschlag.start_date, fahrzeug_ids: [] };
    const sperren = (ids) => pruefeEinsatz({ ...basis, fahrzeug_ids: ids }, kontext).filter((p) => p.schwere === 'sperre').length;
    const ohne = sperren([]);
    const ok = (ids) => sperren(ids) <= ohne;
    const vorhanden = (kontext.fahrzeuge || []);
    const ki = liste(vorschlag.fahrzeug_ids).filter((id) => vorhanden.some((f) => gleich(f.id, id)));
    if (ki.length && ok(ki)) return { fahrzeug_ids: ki, ersetzt: false };

    const teamGroesse = einsatzMitarbeiterIds(vorschlag).length;
    const reihenfolge = { transporter: 0, lkw: 1, pkw: 2 };
    const kandidaten = vorhanden
        .filter((f) => !istAnhaenger(f) && (!f.sitzplaetze || f.sitzplaetze >= teamGroesse))
        .sort((a, b) => (reihenfolge[a.typ] ?? 1) - (reihenfolge[b.typ] ?? 1));
    const frei = kandidaten.find((f) => ok([String(f.id)]));
    return { fahrzeug_ids: frei ? [String(frei.id)] : [], ersetzt: ki.length > 0 };
}
